/** The 30×16 pill that switches a data type on.
 *
 * This is the control the whole service turns on: a server toggle removes a
 * type from `/v1/capabilities` and makes its endpoints answer `403
 * type_disabled`, and a client toggle stops the logger sending. So it is
 * built as a real `role="switch"` button rather than a styled checkbox —
 * it announces its state, it answers Space and Enter, and it carries its
 * description through `aria-describedby` instead of leaving the explanation
 * orphaned beside it.
 *
 * The geometry is the design's and is not negotiable: a 30×16 track, a 12px
 * knob inset 2px, travelling to 16px. On, the track is accent-700 and the
 * knob accent-200; off, neutral-800 and neutral-400.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';

export interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** What the switch controls. Always supply one, even if it is hidden. */
  label: ReactNode;
  /** The sentence under the label explaining what gets sent, or stops being
   *  accepted. Read out as the switch's description. */
  description?: ReactNode;
  /** A word to the right of the row — "accepting", "disabled · 403". */
  state?: ReactNode;
  /** The line under that word — a retention note, a last-synced time. */
  meta?: ReactNode;
  disabled?: boolean;
  /** Hides the label visually but keeps it for assistive technology, for the
   *  rows where the design puts the name in a table cell instead. */
  labelHidden?: boolean;
  className?: string;
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  state,
  meta,
  disabled,
  labelHidden,
  className,
}: ToggleProps) {
  const labelId = useId();
  const descriptionId = useId();

  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: state || meta ? '30px minmax(0,1fr) auto' : '30px minmax(0,1fr)',
        gap: '10px',
        alignItems: 'start',
      }}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
          width: 30,
          height: 16,
          marginTop: 3,
          padding: 0,
          border: 0,
          borderRadius: 8,
          position: 'relative',
          flex: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.45 : 1,
          background: checked ? 'var(--color-accent-700)' : 'var(--color-neutral-800)',
          transition: 'background 140ms ease',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 16 : 2,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: checked ? 'var(--color-accent-200)' : 'var(--color-neutral-400)',
            transition: 'left 140ms ease, background 140ms ease',
          }}
        />
      </button>

      <span style={{ minWidth: 0 }}>
        <span id={labelId} className={labelHidden ? 'sr-only' : undefined} style={{ fontSize: 13.5 }}>
          {label}
        </span>
        {description ? (
          <span
            id={descriptionId}
            style={{
              display: 'block',
              fontSize: 11.5,
              color: 'var(--color-neutral-500)',
              textWrap: 'pretty',
            }}
          >
            {description}
          </span>
        ) : null}
      </span>

      {state || meta ? (
        <span style={{ textAlign: 'right', minWidth: 0 }}>
          {state ? <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{state}</span> : null}
          {meta ? (
            <span
              style={{
                display: 'block',
                fontSize: 11,
                color: 'var(--color-neutral-600)',
                whiteSpace: 'nowrap',
              }}
            >
              {meta}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

export default Toggle;
